import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  map,
  switchMap,
  catchError,
  exhaustMap,
  tap,
  mergeMap,
} from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { AuthenticationService } from '../../core/services/auth.service';
import {
  login,
  loginSuccess,
  loginFailure,
  logout,
  logoutSuccess,
  Register,
  getAgentPayrollSubmissionsFailure,
  getAgentPayrollSubmissionsSuccess,
  getAgentPayrollSubmissions,
  setSharedData,
  getAgentPayrollSubmissionDetailsFailure,
  getAgentPayrollSubmissionDetailsSuccess,
  getAgentPayrollSubmissionDetails,
  updateAgentPayrollSubmissionStatusFailure,
  updateAgentPayrollSubmissionStatusSuccess,
  updateAgentPayrollSubmissionStatus,
} from './authentication.actions';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Action } from '@ngrx/store';
import { PayrollSubmissions } from './auth.models';

@Injectable()
export class AuthenticationEffects {
  Register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Register),
      exhaustMap(({ email, first_name, password }) =>
        this.authService.register(email, first_name, password).pipe(
          map((user) => {
            this.router.navigate(['/auth/login']);
            return loginSuccess();
          }),
          catchError((error) => of(loginFailure({ error })))
        )
      )
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) =>
        this.authService.login(email, password).pipe(
          map((response) => {
            if (response.successful) {
              // localStorage.setItem('currentUser', JSON.stringify(user.data));
              localStorage.setItem('token', response.result.token);
              this.router.navigate(['/']);
            } else {
              this.snackBar.open(response.message, '', {
                duration: 3000,
                panelClass: 'error',
              });
            }
            return loginSuccess();
          }),
          catchError((error) => {
            const errorMsg = error?.error?.message ||error?.message || 'Login failed. Please try again.';
            this.snackBar.open(errorMsg, '', {
              duration: 3000,
              panelClass: 'success',
            });
            return of(loginFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(() => {
        // Perform any necessary cleanup or side effects before logging out
      }),
      exhaustMap(() => of(logoutSuccess()))
    )
  );

  getAgentPayrollSubmissionsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAgentPayrollSubmissions),
      switchMap((action) =>
        this.authService.getAgentPayrollSubmissions(action.data).pipe(
          tap((result) => {
            if (result.successful) {
              // Handle successful result
            }
          }),
          mergeMap((res) => [
            getAgentPayrollSubmissionsSuccess({ data: res }),
            setSharedData({ data: res }),
          ]),
          catchError((error) => {
            return of(getAgentPayrollSubmissionsFailure({ error }));
          })
        )
      )
    );
  });

  getAgentPayrollSubmissionDetailsEffect$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getAgentPayrollSubmissionDetails),
        switchMap((action) =>
          this.authService.getAgentPayrollSubmissionDetails(action.id).pipe(
            mergeMap((res) => [
              getAgentPayrollSubmissionDetailsSuccess({
                data: res,
              }),
            ]),
            catchError((error) => {
              return of(getAgentPayrollSubmissionDetailsFailure({ error }));
            })
          )
        )
      );
    }
  );

  updateAgentPayrollSubmissionStatusEffect$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateAgentPayrollSubmissionStatus),
        switchMap((action) =>
          this.authService.updateAgentPayrollSubmissionStatus(action.data).pipe(
            mergeMap((res) => [
              updateAgentPayrollSubmissionStatusSuccess({ data: res }),
              getAgentPayrollSubmissionDetails({
                id: action.data.submissionId,
              }),
            ]),
            catchError((error) => {
              return of(updateAgentPayrollSubmissionStatusFailure({ error }));
            })
          )
        )
      );
    }
  );

  constructor(
    @Inject(Actions) private actions$: Actions,
    private authService: AuthenticationService,
    private router: Router,
     private snackBar: MatSnackBar
  ) {}
}
